/**
 * Merchant_adminpanel.jsx — Final Production Build
 * ESDRAS MERCHANT MARKETPLACE — Super Admin Control Panel
 * Route: /admin/merchant  |  Access: Super Admins only
 *
 * KEY FIX — GlobalTab now self-fetches grooming data from the exact same
 * Firestore collections as AdminDashboard.jsx:
 *   • admin/finances        → booking commissions, base sub/broadcast/promo
 *   • broadcast_payments    → actual broadcast revenue (overrides finances doc)
 *   • promotion_payments    → actual promotion revenue (overrides finances doc)
 *   • transactions          → subscription breakdown (MONTHLY_SUB / YEARLY_SUB)
 *   • ai_scans              → Rodin call count (@ $0.40/call — matches AdminDashboard)
 *   • style_tryons          → FLUX + Claude call count (@ $0.08 + $0.005)
 *   • admin/merchant_finances → marketplace net
 *   • merchants             → approved merchant count
 *   • Live USD/NGN rate     → open.er-api.com, fallback ₦1,400
 *
 * PAGINATION — every list/table uses cursor-based Firestore pagination (50/page)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit,
  startAfter, getDocs, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const GOLD         = '#B8860B';
const DEEP_BLACK   = '#050a0f';
const CARD_BG      = '#0a0f14';
const CARD_BG2     = '#0d1520';
const BORDER       = '#1a2631';
const PAGE_SIZE    = 50;
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];
const SAFE_IMG     = 'https://placehold.co/60x60/050a0f/B8860B?text=M';
const FALLBACK_RATE = 1400;

// AI cost rates — EXACTLY matching AdminDashboard.jsx
const AI_COSTS_USD = {
  rodin:  0.40,   // Hyper3D Rodin 3D reconstruction
  flux:   0.08,   // FLUX Fill Pro hair inpainting
  claude: 0.005,  // Claude Sonnet vision analysis
};

const TABS = [
  { id: 'approvals', icon: '⏳', label: 'APPROVALS'    },
  { id: 'merchants', icon: '🏪', label: 'MERCHANTS'    },
  { id: 'products',  icon: '📦', label: 'PRODUCTS'     },
  { id: 'orders',    icon: '🛒', label: 'ORDERS'       },
  { id: 'delivery',  icon: '🚚', label: 'DELIVERY'     },
  { id: 'guests',    icon: '👤', label: 'GUEST BUYERS'  },
  { id: 'finances',  icon: '💰', label: 'FINANCES'     },
  { id: 'global',    icon: '🌐', label: 'GLOBAL'       },
];

// ════════════════════════════════════════════════════════════
// UNIVERSAL CURSOR-BASED PAGINATION HOOK (50/page)
// buildQuery() → Firestore Query WITHOUT limit()
// deps[] triggers reset + reload when they change
// ════════════════════════════════════════════════════════════
function usePaginated(buildQuery, deps = []) {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNext,     setHasNext]     = useState(false);
  const cursors = useRef([]);

  const load = useCallback(async (pageIdx, afterDoc) => {
    setLoading(true);
    try {
      const base  = buildQuery();
      const paged = afterDoc
        ? query(base, startAfter(afterDoc), limit(PAGE_SIZE + 1))
        : query(base, limit(PAGE_SIZE + 1));
      const snap  = await getDocs(paged);
      const more  = snap.docs.length > PAGE_SIZE;
      const slice = snap.docs.slice(0, PAGE_SIZE);
      setRows(slice.map(d => ({ id: d.id, ...d.data(), _snap: d })));
      setHasNext(more);
      setCurrentPage(pageIdx);
      if (slice.length > 0) cursors.current[pageIdx] = slice[slice.length - 1];
    } catch (err) {
      console.error('usePaginated:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { cursors.current = []; load(0, null); }, deps); // eslint-disable-line

  const goFirst = () => { cursors.current = []; load(0, null); };
  const goNext  = () => { const c = cursors.current[currentPage]; if (c) load(currentPage + 1, c); };
  const goPrev  = () => {
    if (currentPage === 0) return;
    const c = currentPage >= 2 ? cursors.current[currentPage - 2] : null;
    load(currentPage - 1, c);
  };

  return { rows, loading, currentPage, hasNext, hasPrev: currentPage > 0, goFirst, goNext, goPrev };
}

// ════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ════════════════════════════════════════════════════════════
export default function Merchant_adminpanel() {
  const navigate  = useNavigate();
  const functions = getFunctions();

  const [user,    setUser]    = useState(null);
  const [tab,     setTab]     = useState('approvals');
  const [loading, setLoading] = useState(true);

  // Lightweight real-time counts for sidebar badges only
  const [pendingMCount,    setPendingMCount]    = useState(0);
  const [pendingPCount,    setPendingPCount]    = useState(0);
  const [merchantFinances, setMerchantFinances] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (!u || !SUPER_ADMINS.includes(u.uid)) { navigate('/'); return; }
      setUser(u); setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const subs = [
      onSnapshot(query(collection(db, 'merchants'), where('status', '==', 'pending_approval')),
        s => setPendingMCount(s.size), () => {}),
      onSnapshot(query(collection(db, 'merchant_products'), where('status', '==', 'pending_approval')),
        s => setPendingPCount(s.size), () => {}),
      onSnapshot(doc(db, 'admin', 'merchant_finances'),
        s => { if (s.exists()) setMerchantFinances(s.data()); }, () => {}),
    ];
    return () => subs.forEach(u => u());
  }, [user]);

  if (loading) return <LoadingScreen />;

  return (
    <div style={S.page}>
      {/* Sidebar */}
      <aside style={S.sidebar}>
        <div style={S.sideTop}>
          <img src="/icons/esdras-logo.png" alt="" style={{ width: '34px', marginBottom: '6px' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <p style={S.sideTitle}>MERCHANT ADMIN</p>
        </div>
        <nav style={S.nav}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ ...S.navBtn, ...(tab === t.id ? S.navActive : {}) }}>
              <span>{t.icon}</span>
              <span style={{ flex: 1 }}>{t.label}</span>
              {t.id === 'approvals' && (pendingMCount + pendingPCount) > 0 && (
                <span style={S.badge}>{pendingMCount + pendingPCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div style={{ padding: '10px 8px', borderTop: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button onClick={() => navigate('/admin')} style={S.navLink}>← Grooming Admin</button>
          <button onClick={() => navigate('/admin/dashboard')} style={S.navLink}>📊 Grooming Finances</button>
        </div>
      </aside>

      {/* Main */}
      <main style={S.main}>
        {tab === 'approvals' && <ApprovalsTab  functions={functions} />}
        {tab === 'merchants' && <MerchantsTab />}
        {tab === 'products'  && <ProductsTab   functions={functions} />}
        {tab === 'orders'    && <OrdersTab />}
        {tab === 'delivery'  && <DeliveryTab   functions={functions} />}
        {tab === 'guests'    && <GuestBuyersTab />}
        {tab === 'finances'  && <FinancesTab   merchantFinances={merchantFinances} />}
        {tab === 'global'    && <GlobalTab     merchantFinances={merchantFinances} />}
      </main>
      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// APPROVALS TAB
// ════════════════════════════════════════════════════════════
function ApprovalsTab({ functions }) {
  const [rejectId,     setRejectId]     = useState(null);
  const [rejectType,   setRejectType]   = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [proc,         setProc]         = useState({});

  const mPag = usePaginated(
    () => query(collection(db, 'merchants'), where('status', '==', 'pending_approval'), orderBy('createdAt', 'asc')), []);
  const pPag = usePaginated(
    () => query(collection(db, 'merchant_products'), where('status', '==', 'pending_approval'), orderBy('createdAt', 'asc')), []);

  const approve = async (type, id) => {
    setProc(p => ({ ...p, [id]: true }));
    try {
      const fnName = type === 'merchant' ? 'merchantApproveProfile' : 'merchantApproveProduct';
      const key    = type === 'merchant' ? 'merchantId' : 'productId';
      const r      = await httpsCallable(functions, fnName)({ [key]: id });
      if (r.data?.success) {
        toast.success(`${type === 'merchant' ? 'Merchant' : 'Product'} approved!`);
        type === 'merchant' ? mPag.goFirst() : pPag.goFirst();
      } else toast.error(r.data?.message || 'Failed.');
    } catch (e) { toast.error(e.message); }
    finally { setProc(p => ({ ...p, [id]: false })); }
  };

  const sendReject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required.'); return; }
    setProc(p => ({ ...p, [rejectId]: true }));
    try {
      const fnName = rejectType === 'merchant' ? 'merchantRejectProfile' : 'merchantRejectProduct';
      const pl     = rejectType === 'merchant'
        ? { merchantId: rejectId, rejectionReason: rejectReason }
        : { productId:  rejectId, rejectionReason: rejectReason };
      const r = await httpsCallable(functions, fnName)(pl);
      if (r.data?.success) {
        toast.success('Rejection sent.');
        setRejectId(null); setRejectReason('');
        rejectType === 'merchant' ? mPag.goFirst() : pPag.goFirst();
      } else toast.error(r.data?.message || 'Failed.');
    } catch (e) { toast.error(e.message); }
    finally { setProc(p => ({ ...p, [rejectId]: false })); }
  };

  return (
    <div style={S.content}>
      {rejectId && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={{ color: '#ff4d4d', margin: '0 0 10px' }}>REJECT {rejectType?.toUpperCase()}</h3>
            <p style={{ color: '#888', fontSize: '0.82rem', margin: '0 0 12px', lineHeight: '1.5' }}>
              This message is emailed directly to the {rejectType}. Be specific.
            </p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              placeholder="e.g. NAFDAC number not verifiable. Please resubmit with valid documentation."
              style={{ ...S.input, minHeight: '100px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button onClick={sendReject} style={{ flex: 1, ...S.rejectBtn }}>SEND REJECTION</button>
              <button onClick={() => { setRejectId(null); setRejectReason(''); }} style={{ flex: 1, ...S.outlineBtn }}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      <TabHeader title="PENDING APPROVALS" sub="Merchants and products awaiting review" />

      <SectionLabel>MERCHANT REGISTRATIONS · Page {mPag.currentPage + 1}</SectionLabel>
      {mPag.loading ? <Spinner /> : mPag.rows.length === 0 ? (
        <Empty icon="✅" title="All clear" sub="No pending merchant registrations." />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '6px' }}>
            {mPag.rows.map(m => (
              <div key={m.id} style={S.approvalCard}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <Avatar src={m.logoUrl} name={m.businessName} />
                  <div style={{ flex: 1 }}>
                    <p style={{ color: 'white', fontWeight: '700', margin: '0 0 2px' }}>{m.businessName}</p>
                    <p style={{ color: '#555', fontSize: '0.72rem', margin: 0 }}>
                      {m.merchantType} · {m.categories?.map(c => c.replace('_', ' ')).join(' & ')}
                    </p>
                  </div>
                  <p style={{ color: '#444', fontSize: '0.67rem', whiteSpace: 'nowrap' }}>
                    {m.createdAt?.toDate?.().toLocaleDateString('en-NG') || ''}
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '12px' }}>
                  {[['Email', m.email], ['Phone', m.phone], ['City', m.city],
                    ['Gender', m.gender], ['CAC', m.cacNumber || 'Not provided'],
                    ['Bank', `${m.bankName || '—'} · ${m.bankAccountNumber || '—'}`]].map(([k, v]) => (
                    <div key={k} style={{ background: CARD_BG2, borderRadius: '8px', padding: '7px 10px' }}>
                      <p style={{ color: '#444', fontSize: '0.58rem', margin: '0 0 2px', letterSpacing: '1px' }}>{k.toUpperCase()}</p>
                      <p style={{ color: '#aaa', fontSize: '0.73rem', margin: 0, wordBreak: 'break-all' }}>{v || '—'}</p>
                    </div>
                  ))}
                </div>
                {m.businessRegDocUrl && (
                  <a href={m.businessRegDocUrl} target="_blank" rel="noreferrer"
                    style={{ color: GOLD, fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '5px', marginBottom: '10px' }}>
                    📄 View Registration Doc ↗
                  </a>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => approve('merchant', m.id)} disabled={proc[m.id]}
                    style={{ flex: 1, ...S.approveBtn, opacity: proc[m.id] ? 0.6 : 1 }}>
                    {proc[m.id] ? '…' : '✅ APPROVE'}
                  </button>
                  <button onClick={() => { setRejectId(m.id); setRejectType('merchant'); }}
                    style={{ flex: 1, ...S.rejectBtn }}>❌ REJECT</button>
                </div>
              </div>
            ))}
          </div>
          <Paginator {...mPag} />
        </>
      )}

      <SectionLabel style={{ marginTop: '32px' }}>PRODUCT LISTINGS · Page {pPag.currentPage + 1}</SectionLabel>
      {pPag.loading ? <Spinner /> : pPag.rows.length === 0 ? (
        <Empty icon="✅" title="All clear" sub="No product listings awaiting approval." />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '12px', marginBottom: '6px' }}>
            {pPag.rows.map(p => (
              <div key={p.id} style={S.prodCard}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                  <img src={p.imageUrls?.[0] || SAFE_IMG} alt="" style={{ width: '68px', height: '68px', borderRadius: '10px', objectFit: 'cover' }}
                    onError={e => { e.target.src = SAFE_IMG; }} />
                  <div>
                    <p style={{ color: 'white', fontWeight: '700', margin: '0 0 2px', fontSize: '0.9rem' }}>{p.name}</p>
                    <p style={{ color: '#555', fontSize: '0.7rem', margin: '0 0 2px' }}>{p.merchantName}</p>
                    <p style={{ color: '#444', fontSize: '0.66rem', margin: 0 }}>{p.category?.replace('_', ' ')}</p>
                  </div>
                </div>
                {[['NAFDAC', p.nafdacNumber], ['Base Price', `₦${Number(p.basePrice || 0).toLocaleString()}`],
                  ['Buyer Price', `₦${Number(p.displayPrice || 0).toLocaleString()}`], ['Qty', p.qty]].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${BORDER}` }}>
                    <span style={{ color: '#444', fontSize: '0.67rem' }}>{k}</span>
                    <span style={{ color: '#aaa', fontSize: '0.73rem' }}>{v}</span>
                  </div>
                ))}
                <p style={{ color: '#666', fontSize: '0.72rem', margin: '8px 0', lineHeight: '1.5' }}>
                  {p.description?.substring(0, 100)}{p.description?.length > 100 ? '…' : ''}
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => approve('product', p.id)} disabled={proc[p.id]}
                    style={{ flex: 1, ...S.approveBtn, opacity: proc[p.id] ? 0.6 : 1 }}>
                    {proc[p.id] ? '…' : '✅ APPROVE'}
                  </button>
                  <button onClick={() => { setRejectId(p.id); setRejectType('product'); }}
                    style={{ flex: 1, ...S.rejectBtn }}>❌ REJECT</button>
                </div>
              </div>
            ))}
          </div>
          <Paginator {...pPag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MERCHANTS TAB
// ════════════════════════════════════════════════════════════
function MerchantsTab() {
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [cat,    setCat]    = useState('all');
  const SC = { approved: '#4caf50', pending_approval: '#f59e0b', rejected: '#ff4d4d', suspended: '#a855f7' };

  const buildQ = useCallback(() => {
    if (status !== 'all') return query(collection(db, 'merchants'), where('status', '==', status), orderBy('createdAt', 'desc'));
    return query(collection(db, 'merchants'), orderBy('createdAt', 'desc'));
  }, [status]);

  const pag = usePaginated(buildQ, [status]);

  const visible = pag.rows.filter(m => {
    if (cat !== 'all' && !m.categories?.includes(cat)) return false;
    const q = search.toLowerCase();
    return !q || [m.businessName, m.contactName, m.email, m.phone, m.address, m.city].some(f => f?.toLowerCase().includes(q));
  });

  return (
    <div style={S.content}>
      <TabHeader title="ALL MERCHANTS" sub={`${pag.rows.length} on this page (max ${PAGE_SIZE})`} />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input placeholder="Search name, email, phone, address…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '300px' }} />
        <select value={cat} onChange={e => setCat(e.target.value)} style={{ ...S.select, maxWidth: '160px' }}>
          <option value="all">All Categories</option>
          <option value="hair_care">Hair Care</option>
          <option value="skin_care">Skin Care</option>
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...S.select, maxWidth: '160px' }}>
          <option value="all">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="pending_approval">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>
      <p style={{ color: '#333', fontSize: '0.68rem', margin: '0 0 12px' }}>
        Showing {visible.length}/{pag.rows.length} · Page {pag.currentPage + 1} · Change status to paginate all
      </p>
      {pag.loading ? <Spinner /> : visible.length === 0 ? (
        <Empty icon="🏪" title="No merchants" sub="Adjust your search or filters." />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '6px' }}>
            {visible.map(m => (
              <div key={m.id} style={S.rowCard}>
                <Avatar src={m.logoUrl} name={m.businessName} />
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 2fr 2fr 1fr', gap: '8px', alignItems: 'center', minWidth: 0 }}>
                  <div>
                    <p style={{ color: 'white', fontWeight: '700', fontSize: '0.85rem', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.businessName}</p>
                    <p style={{ color: '#444', fontSize: '0.66rem', margin: 0 }}>{m.contactName}</p>
                  </div>
                  <div>
                    <p style={{ color: '#aaa', fontSize: '0.72rem', margin: '0 0 2px' }}>{m.email}</p>
                    <p style={{ color: '#444', fontSize: '0.66rem', margin: 0 }}>{m.phone}</p>
                  </div>
                  <div>
                    <p style={{ color: '#aaa', fontSize: '0.72rem', margin: '0 0 2px' }}>{m.city || m.address}</p>
                    <p style={{ color: '#333', fontSize: '0.64rem', margin: 0 }}>{m.categories?.map(c => c.replace('_', ' ')).join(', ')}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ background: (SC[m.status] || '#888') + '22', color: SC[m.status] || '#888', fontSize: '0.58rem', fontWeight: '900', padding: '3px 8px', borderRadius: '20px', border: `1px solid ${(SC[m.status] || '#888')}44` }}>
                      {m.status?.replace('_', ' ').toUpperCase()}
                    </span>
                    <p style={{ color: '#333', fontSize: '0.62rem', margin: '4px 0 0' }}>{m.totalProductsLive || 0} live</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Paginator {...pag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PRODUCTS TAB
// ════════════════════════════════════════════════════════════
function ProductsTab({ functions }) {
  const [statusF, setStatusF] = useState('approved');
  const [catF,    setCatF]    = useState('all');
  const [search,  setSearch]  = useState('');
  const [proc,    setProc]    = useState({});

  const buildQ = useCallback(() => {
    if (statusF !== 'all') return query(collection(db, 'merchant_products'), where('status', '==', statusF), orderBy('createdAt', 'desc'));
    return query(collection(db, 'merchant_products'), orderBy('createdAt', 'desc'));
  }, [statusF]);

  const pag = usePaginated(buildQ, [statusF]);
  const visible = pag.rows.filter(p => {
    if (catF !== 'all' && p.category !== catF) return false;
    const q = search.toLowerCase();
    return !q || [p.name, p.merchantName, p.nafdacNumber].some(f => f?.toLowerCase().includes(q));
  });

  const toggleLive = async (prod) => {
    setProc(p => ({ ...p, [prod.id]: true }));
    try {
      await updateDoc(doc(db, 'merchant_products', prod.id), { liveOnHomepage: !prod.liveOnHomepage, updatedAt: serverTimestamp() });
      toast.success(prod.liveOnHomepage ? 'Hidden from homepage.' : 'Now live on homepage.');
      pag.goFirst();
    } catch { toast.error('Update failed.'); }
    finally { setProc(p => ({ ...p, [prod.id]: false })); }
  };

  return (
    <div style={S.content}>
      <TabHeader title="ALL PRODUCTS" sub={`${pag.rows.length} on this page`} />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input placeholder="Search product, merchant, NAFDAC…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '280px' }} />
        <select value={catF} onChange={e => setCatF(e.target.value)} style={{ ...S.select, maxWidth: '150px' }}>
          <option value="all">All Categories</option>
          <option value="hair_care">Hair Care</option>
          <option value="skin_care">Skin Care</option>
        </select>
        <select value={statusF} onChange={e => setStatusF(e.target.value)} style={{ ...S.select, maxWidth: '150px' }}>
          <option value="all">All</option>
          <option value="approved">Approved</option>
          <option value="pending_approval">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      {pag.loading ? <Spinner /> : visible.length === 0 ? (
        <Empty icon="📦" title="No products" sub="No products match the current filter." />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '12px', marginBottom: '6px' }}>
            {visible.map(p => (
              <div key={p.id} style={S.prodCard}>
                <div style={{ position: 'relative', aspectRatio: '1/1', background: CARD_BG2, borderRadius: '10px', overflow: 'hidden', marginBottom: '10px' }}>
                  <img src={p.imageUrls?.[0] || SAFE_IMG} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = SAFE_IMG; }} />
                  <div style={{ position: 'absolute', top: '6px', right: '6px', background: p.liveOnHomepage ? '#0a1f0a' : '#1a0a0a', color: p.liveOnHomepage ? '#4caf50' : '#ff4d4d', fontSize: '0.57rem', fontWeight: '900', padding: '3px 7px', borderRadius: '10px' }}>
                    {p.liveOnHomepage ? '● LIVE' : '● HIDDEN'}
                  </div>
                </div>
                <p style={{ color: 'white', fontWeight: '700', fontSize: '0.87rem', margin: '0 0 2px' }}>{p.name}</p>
                <p style={{ color: '#555', fontSize: '0.67rem', margin: '0 0 8px' }}>{p.merchantName}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ color: GOLD, fontWeight: '900' }}>₦{Number(p.displayPrice || 0).toLocaleString()}</span>
                  <span style={{ color: '#555', fontSize: '0.7rem' }}>Qty: {p.qty}</span>
                </div>
                {p.status === 'approved' && (
                  <button onClick={() => toggleLive(p)} disabled={proc[p.id]}
                    style={{ ...S.outlineBtn, fontSize: '0.68rem', padding: '7px', width: '100%', opacity: proc[p.id] ? 0.6 : 1 }}>
                    {p.liveOnHomepage ? 'HIDE FROM HOMEPAGE' : 'SHOW ON HOMEPAGE'}
                  </button>
                )}
              </div>
            ))}
          </div>
          <Paginator {...pag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ORDERS TAB
// ════════════════════════════════════════════════════════════
function OrdersTab() {
  const [statusF, setStatusF] = useState('all');
  const OSC = { paid: '#f59e0b', processing: GOLD, dispatched: '#60a5fa', delivered: '#4caf50', received: '#888' };

  const buildQ = useCallback(() => {
    if (statusF !== 'all') return query(collection(db, 'merchant_orders'), where('orderStatus', '==', statusF), orderBy('createdAt', 'desc'));
    return query(collection(db, 'merchant_orders'), orderBy('createdAt', 'desc'));
  }, [statusF]);

  const pag = usePaginated(buildQ, [statusF]);

  return (
    <div style={S.content}>
      <TabHeader title="ALL ORDERS" sub={`${pag.rows.length} on this page`} />
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[['all','All'],['paid','Paid'],['processing','Processing'],['dispatched','Dispatched'],['delivered','Delivered'],['received','Received']].map(([v,l]) => (
          <button key={v} onClick={() => setStatusF(v)}
            style={{ ...S.pill, ...(statusF === v ? S.pillActive : {}) }}>{l}</button>
        ))}
      </div>
      {pag.loading ? <Spinner /> : pag.rows.length === 0 ? (
        <Empty icon="🛒" title="No orders" sub="Orders appear here after purchases." />
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginBottom: '6px' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Order ID','Product','Merchant','Buyer','Qty','Total','Payment','Status','Date'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pag.rows.map((o, i) => {
                  const c = OSC[o.orderStatus] || '#aaa';
                  return (
                    <tr key={o.id} style={{ background: i % 2 ? '#060c1180' : 'transparent' }}>
                      <td style={S.td}><span style={{ color: GOLD, fontFamily: 'monospace', fontSize: '0.67rem' }}>{o.orderId?.substring(0, 14)}…</span></td>
                      <td style={{ ...S.td, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.productName}</td>
                      <td style={S.td}>{o.merchantName}</td>
                      <td style={{ ...S.td, fontSize: '0.72rem' }}>{o.buyerName}<br /><span style={{ color: '#444', fontSize: '0.65rem' }}>{o.buyerEmail}</span></td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{o.qty}</td>
                      <td style={{ ...S.td, color: GOLD, fontWeight: '700' }}>₦{Number(o.totalPaid || 0).toLocaleString()}</td>
                      <td style={{ ...S.td, textTransform: 'uppercase', fontSize: '0.65rem' }}>{o.paymentMethod}</td>
                      <td style={S.td}><span style={{ background: c + '22', color: c, fontSize: '0.58rem', fontWeight: '900', padding: '3px 8px', borderRadius: '20px', border: `1px solid ${c}44` }}>{o.orderStatus}</span></td>
                      <td style={{ ...S.td, color: '#555', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>{o.createdAt?.toDate?.().toLocaleDateString('en-NG') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paginator {...pag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// DELIVERY TAB
// ════════════════════════════════════════════════════════════
function DeliveryTab({ functions }) {
  const [filter, setFilter] = useState('needs_cost');
  const [costs,  setCosts]  = useState({});
  const [proc,   setProc]   = useState({});

  const buildQ = useCallback(() => {
    if (filter === 'needs_cost') return query(collection(db, 'merchant_orders'), where('paymentStatus', '==', 'paid'), where('actualDeliveryCost', '==', null), orderBy('createdAt', 'asc'));
    if (filter === 'costed')     return query(collection(db, 'merchant_orders'), where('actualDeliveryCost', '!=', null), orderBy('actualDeliveryCost', 'desc'));
    return query(collection(db, 'merchant_orders'), where('paymentStatus', '==', 'paid'), orderBy('createdAt', 'desc'));
  }, [filter]);

  const pag = usePaginated(buildQ, [filter]);

  const logCost = async (orderId) => {
    const cost = Number(costs[orderId]);
    if (!cost || cost < 0) { toast.error('Enter a valid cost.'); return; }
    setProc(p => ({ ...p, [orderId]: true }));
    try {
      const r = await httpsCallable(functions, 'merchantLogDeliveryCost')({ orderId, actualDeliveryCost: cost });
      if (r.data?.success) {
        toast.success(`Net profit: ₦${Number(r.data.deliveryNetProfit).toLocaleString()}`);
        setCosts(p => ({ ...p, [orderId]: '' }));
        pag.goFirst();
      } else toast.error('Log failed.');
    } catch (e) { toast.error(e.message); }
    finally { setProc(p => ({ ...p, [orderId]: false })); }
  };

  return (
    <div style={S.content}>
      <TabHeader title="DELIVERY MANAGEMENT" sub="Log actual costs to track net delivery profit" />
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[['needs_cost','Needs Cost Entry'],['costed','Cost Logged'],['all','All Paid']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ ...S.pill, ...(filter === v ? S.pillActive : {}) }}>{l}</button>
        ))}
      </div>
      {pag.loading ? <Spinner /> : pag.rows.length === 0 ? (
        <Empty icon="🚚" title="No orders" sub={filter === 'needs_cost' ? 'All paid orders have delivery costs logged.' : 'No orders found.'} />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '6px' }}>
            {pag.rows.map(o => (
              <div key={o.id} style={S.rowCard}>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <p style={{ color: GOLD, fontFamily: 'monospace', fontSize: '0.77rem', margin: '0 0 2px', fontWeight: '700' }}>{o.orderId}</p>
                    <p style={{ color: 'white', fontWeight: '700', margin: '0 0 2px', fontSize: '0.9rem' }}>{o.productName}</p>
                    <p style={{ color: '#555', fontSize: '0.72rem', margin: '0 0 2px' }}>{o.buyerName} · {o.buyerPhone}</p>
                    <p style={{ color: '#444', fontSize: '0.7rem', margin: 0 }}>📍 {o.deliveryAddress}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ color: GOLD, fontWeight: '900', margin: '0 0 2px' }}>₦{Number(o.deliveryFee || 0).toLocaleString()} collected</p>
                    {o.actualDeliveryCost != null ? (
                      <>
                        <p style={{ color: '#888', fontSize: '0.72rem', margin: '0 0 2px' }}>Actual: ₦{Number(o.actualDeliveryCost).toLocaleString()}</p>
                        <p style={{ color: (o.deliveryNetProfit || 0) >= 0 ? '#4caf50' : '#ff4d4d', fontSize: '0.78rem', fontWeight: '700', margin: 0 }}>Net: ₦{Number(o.deliveryNetProfit || 0).toLocaleString()}</p>
                      </>
                    ) : <span style={{ color: '#f59e0b', fontSize: '0.68rem', fontWeight: '700' }}>COST PENDING</span>}
                  </div>
                </div>
                {o.actualDeliveryCost == null && (
                  <div style={{ display: 'flex', gap: '8px', borderTop: `1px solid ${BORDER}`, paddingTop: '10px', width: '100%' }}>
                    <input type="number" placeholder="Actual delivery cost (₦)" value={costs[o.id] || ''}
                      onChange={e => setCosts(p => ({ ...p, [o.id]: e.target.value }))}
                      style={{ ...S.input, flex: 1, padding: '9px 12px' }} />
                    <button onClick={() => logCost(o.id)} disabled={proc[o.id]}
                      style={{ ...S.goldBtn, padding: '9px 16px', fontSize: '0.78rem', opacity: proc[o.id] ? 0.6 : 1 }}>
                      {proc[o.id] ? '…' : 'LOG'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Paginator {...pag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// GUEST BUYERS TAB
// ════════════════════════════════════════════════════════════
function GuestBuyersTab() {
  const [search,    setSearch]    = useState('');
  const [exporting, setExporting] = useState(false);

  const buildQ = useCallback(
    () => query(collection(db, 'marketplace_guest_buyers'), orderBy('createdAt', 'desc')), []);
  const pag = usePaginated(buildQ, []);

  const visible = pag.rows.filter(g => {
    const q = search.toLowerCase();
    return !q || [g.name, g.email, g.phone, g.productName].some(f => f?.toLowerCase().includes(q));
  });

  const exportCSV = async () => {
    setExporting(true);
    try {
      const snap = await getDocs(query(collection(db, 'marketplace_guest_buyers'), orderBy('createdAt', 'desc'), limit(5000)));
      const all  = snap.docs.map(d => d.data());
      const rows = [
        ['Name', 'Email', 'Phone', 'Order ID', 'Product', 'Date'],
        ...all.map(g => [g.name || '', g.email || '', g.phone || '', g.orderId || '', g.productName || '',
          g.createdAt?.toDate?.().toLocaleDateString('en-NG') || '']),
      ];
      const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `EsdrasGuestBuyers_${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${all.length} guest buyers.`);
    } catch (e) { toast.error('Export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  return (
    <div style={S.content}>
      <TabHeader title="GUEST BUYERS" sub="Email-verified non-account buyers" action={
        <button onClick={exportCSV} disabled={exporting} style={S.exportBtn}>
          {exporting ? 'EXPORTING…' : '⬇ EXPORT CSV'}
        </button>
      } />
      <input placeholder="Search name, email, phone, product…" value={search}
        onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '340px', marginBottom: '12px' }} />
      {pag.loading ? <Spinner /> : visible.length === 0 ? (
        <Empty icon="👤" title="No guest buyers" sub="Appears here once guests complete email verification and purchase." />
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginBottom: '6px' }}>
            <table style={S.table}>
              <thead>
                <tr>{['Name','Email','Phone','Product','Order ID','Date'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {visible.map((g, i) => (
                  <tr key={g.id || i} style={{ background: i % 2 ? '#060c1180' : 'transparent' }}>
                    <td style={S.td}>{g.name}</td>
                    <td style={{ ...S.td, color: GOLD }}>{g.email}</td>
                    <td style={S.td}>{g.phone}</td>
                    <td style={S.td}>{g.productName}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '0.67rem', color: '#555' }}>{g.orderId?.substring(0, 16)}…</td>
                    <td style={{ ...S.td, color: '#555', fontSize: '0.7rem' }}>{g.createdAt?.toDate?.().toLocaleDateString('en-NG') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator {...pag} label={`Page ${pag.currentPage + 1} · ${visible.length}/${pag.rows.length} shown`} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// FINANCES TAB
// ════════════════════════════════════════════════════════════
function FinancesTab({ merchantFinances }) {
  const buildQ = useCallback(
    () => query(collection(db, 'merchant_orders'), where('paymentStatus', '==', 'paid'), orderBy('createdAt', 'desc')), []);
  const pag = usePaginated(buildQ, []);

  const f   = merchantFinances || {};
  const mktNet = (f.totalPlatformFeesCollected || 0) + (f.totalDeliveryNetProfit || 0);
  const stats = [
    { l: 'PLATFORM FEES',      v: f.totalPlatformFeesCollected || 0, c: GOLD,      note: '10% of base prices' },
    { l: 'DELIVERY COLLECTED', v: f.totalDeliveryFeesCollected  || 0, c: '#60a5fa', note: 'From buyers' },
    { l: 'ACTUAL DELIVERY',    v: f.totalActualDeliveryCosts    || 0, c: '#f59e0b', note: 'Logged costs' },
    { l: 'DELIVERY NET',       v: f.totalDeliveryNetProfit      || 0, c: (f.totalDeliveryNetProfit || 0) >= 0 ? '#4caf50' : '#ff4d4d', note: 'Collected − actual' },
    { l: 'MARKETPLACE NET',    v: mktNet,                             c: GOLD,      note: 'Platform + delivery net', big: true },
    { l: 'TOTAL ORDERS',       v: f.totalOrdersCount || 0,            c: '#aaa',    raw: true },
  ];

  return (
    <div style={S.content}>
      <TabHeader title="MARKETPLACE FINANCES" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: '12px', marginBottom: '28px' }}>
        {stats.map(s => (
          <div key={s.l} style={{ background: CARD_BG, borderRadius: '14px', padding: '16px', border: s.big ? `1px solid ${GOLD}55` : `1px solid ${BORDER}` }}>
            <p style={{ color: s.c, fontWeight: '900', fontSize: s.big ? '1.8rem' : '1.3rem', margin: '0 0 4px' }}>
              {s.raw ? s.v : `₦${Number(s.v).toLocaleString()}`}
            </p>
            <p style={{ color: '#444', fontSize: '0.6rem', letterSpacing: '1.5px', margin: '0 0 2px' }}>{s.l}</p>
            {s.note && <p style={{ color: '#2a2a2a', fontSize: '0.62rem', margin: 0 }}>{s.note}</p>}
          </div>
        ))}
      </div>

      <SectionLabel>ORDER BREAKDOWN · Page {pag.currentPage + 1}</SectionLabel>
      {pag.loading ? <Spinner /> : (
        <>
          <div style={{ overflowX: 'auto', marginBottom: '6px' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Order ID','Product','Merchant','Item Total','Platform Fee','Delivery Collected','Actual Delivery','Net Delivery','Date'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pag.rows.length === 0 ? (
                  <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', padding: '30px', color: '#444' }}>No paid orders yet.</td></tr>
                ) : pag.rows.map((o, i) => (
                  <tr key={o.id} style={{ background: i % 2 ? '#060c1180' : 'transparent' }}>
                    <td style={S.td}><span style={{ color: GOLD, fontFamily: 'monospace', fontSize: '0.67rem' }}>{o.orderId?.substring(0, 14)}…</span></td>
                    <td style={{ ...S.td, maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.productName}</td>
                    <td style={S.td}>{o.merchantName}</td>
                    <td style={S.td}>₦{((o.displayPrice || 0) * (o.qty || 1)).toLocaleString()}</td>
                    <td style={{ ...S.td, color: GOLD }}>₦{((o.platformFee || 0) * (o.qty || 1)).toLocaleString()}</td>
                    <td style={S.td}>₦{Number(o.deliveryFee || 0).toLocaleString()}</td>
                    <td style={{ ...S.td, color: o.actualDeliveryCost != null ? '#aaa' : '#f59e0b' }}>
                      {o.actualDeliveryCost != null ? `₦${Number(o.actualDeliveryCost).toLocaleString()}` : 'Pending'}
                    </td>
                    <td style={{ ...S.td, color: (o.deliveryNetProfit || 0) >= 0 ? '#4caf50' : '#ff4d4d' }}>
                      {o.deliveryNetProfit != null ? `₦${Number(o.deliveryNetProfit).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ ...S.td, color: '#555', whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                      {o.createdAt?.toDate?.().toLocaleDateString('en-NG') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator {...pag} />
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// GLOBAL TAB — Self-fetching, mirrors AdminDashboard.jsx exactly
// ════════════════════════════════════════════════════════════
function GlobalTab({ merchantFinances }) {
  // ── Live USD/NGN rate ────────────────────────────────────────────────────
  const [usdToNgn,      setUsdToNgn]      = useState(FALLBACK_RATE);
  const [rateUpdatedAt, setRateUpdatedAt] = useState(null);

  // ── Grooming revenue state (mirrors AdminDashboard) ──────────────────────
  const [groomFinancesDoc, setGroomFinancesDoc] = useState({});
  const [broadcastRev,     setBroadcastRev]     = useState(0);
  const [promotionRev,     setPromotionRev]     = useState(0);
  const [subRev,           setSubRev]           = useState({ monthly: 0, yearly: 0 });

  // ── AI usage counts (from actual collections, same as AdminDashboard) ────
  const [rodinCount,  setRodinCount]  = useState(0);
  const [tryonCount,  setTryonCount]  = useState(0);

  // ── Merchant count ───────────────────────────────────────────────────────
  const [approvedMerchantCount, setApprovedMerchantCount] = useState(0);

  // ── Fetch live exchange rate ─────────────────────────────────────────────
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res  = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await res.json();
        if (data?.rates?.NGN) { setUsdToNgn(data.rates.NGN); setRateUpdatedAt(new Date()); }
      } catch { /* keep fallback */ }
    };
    fetchRate();
    const iv = setInterval(fetchRate, 60 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // ── All grooming data listeners — exactly as AdminDashboard.jsx ──────────
  useEffect(() => {
    const subs = [];

    // 1. admin/finances document (booking commissions + base values)
    subs.push(onSnapshot(doc(db, 'admin', 'finances'), snap => {
      if (snap.exists()) setGroomFinancesDoc(snap.data());
    }, () => {}));

    // 2. broadcast_payments collection — ACTUAL broadcast revenue
    subs.push(onSnapshot(
      query(collection(db, 'broadcast_payments'), where('status', '==', 'paid')),
      snap => setBroadcastRev(snap.docs.reduce((s, d) => s + (d.data().amount || 0), 0)),
      () => {},
    ));

    // 3. promotion_payments collection — ACTUAL promotion revenue
    subs.push(onSnapshot(
      query(collection(db, 'promotion_payments'), where('status', '==', 'paid')),
      snap => setPromotionRev(snap.docs.reduce((s, d) => s + (d.data().amount || 0), 0)),
      () => {},
    ));

    // 4. transactions collection — subscription revenue breakdown
    subs.push(onSnapshot(
      query(
        collection(db, 'transactions'),
        where('type', 'in', ['MONTHLY_SUB', 'YEARLY_SUB', 'PREMIUM_UPGRADE']),
        where('status', '==', 'SUCCESS'),
      ),
      snap => {
        let monthly = 0, yearly = 0;
        snap.docs.forEach(d => {
          const data = d.data();
          if      (data.type === 'MONTHLY_SUB')  monthly += (data.amount || 0);
          else if (data.type === 'YEARLY_SUB')   yearly  += (data.amount || 0);
          else if ((data.amount || 0) > 5000)    yearly  += (data.amount || 0); // PREMIUM_UPGRADE heuristic
          else                                   monthly += (data.amount || 0);
        });
        setSubRev({ monthly, yearly });
      },
      () => {},
    ));

    // 5. ai_scans — Rodin call count (same as AdminDashboard allTimeRodinCount)
    subs.push(onSnapshot(
      query(collection(db, 'ai_scans'), limit(5000)),
      snap => setRodinCount(snap.size),
      () => {},
    ));

    // 6. style_tryons — FLUX + Claude call count (same as AdminDashboard allTimeTryonCount)
    subs.push(onSnapshot(
      query(collection(db, 'style_tryons'), limit(5000)),
      snap => setTryonCount(snap.size),
      () => {},
    ));

    // 7. Approved merchant count
    subs.push(onSnapshot(
      query(collection(db, 'merchants'), where('status', '==', 'approved')),
      snap => setApprovedMerchantCount(snap.size),
      () => {},
    ));

    return () => subs.forEach(u => u());
  }, []);

  // ── Grooming revenue computation (mirrors AdminDashboard exactly) ────────
  const bookingComm    = groomFinancesDoc.totalCommissions   || 0;
  // Use collection-level values as primary; fall back to finances doc (same as AdminDashboard)
  const monthlySubRev  = subRev.monthly  || groomFinancesDoc.monthlySubRevenue  || 0;
  const yearlySubRev   = subRev.yearly   || groomFinancesDoc.yearlySubRevenue   || 0;
  const subRevTotal    = monthlySubRev + yearlySubRev;
  const broadcastTotal = broadcastRev    || groomFinancesDoc.broadcastRevenue   || 0;
  const promotionTotal = promotionRev    || groomFinancesDoc.promotionRevenue   || groomFinancesDoc.boostRevenue || 0;
  const groomNet       = bookingComm + subRevTotal + broadcastTotal + promotionTotal;

  // ── Marketplace revenue ──────────────────────────────────────────────────
  const mf      = merchantFinances || {};
  const mktFees = mf.totalPlatformFeesCollected || 0;
  const mktDNet = mf.totalDeliveryNetProfit     || 0;
  const mktNet  = mktFees + mktDNet;

  const combined = groomNet + mktNet;

  // ── AI costs (same formula as AdminDashboard) ────────────────────────────
  const rodinCostUSD  = rodinCount  * AI_COSTS_USD.rodin;
  const fluxCostUSD   = tryonCount  * AI_COSTS_USD.flux;
  const claudeCostUSD = tryonCount  * AI_COSTS_USD.claude;
  const totalAIusd    = rodinCostUSD + fluxCostUSD + claudeCostUSD;
  const totalAIngn    = totalAIusd * usdToNgn;

  const netProfit = combined - totalAIngn;

  const fmt = (n) => `₦${Math.round(n).toLocaleString()}`;

  const statCards = [
    { l: 'GROOMING REVENUE',    v: fmt(groomNet),    c: '#60a5fa', note: 'Bookings + subs + broadcasts + promotions' },
    { l: 'MARKETPLACE REVENUE', v: fmt(mktNet),      c: GOLD,      note: 'Platform fees + delivery net profit' },
    { l: 'COMBINED REVENUE',    v: fmt(combined),    c: GOLD,      big: true },
    { l: 'AI COSTS (NGN)',       v: fmt(totalAIngn), c: '#ff4d4d', note: `$${totalAIusd.toFixed(2)} USD · $1=₦${Math.round(usdToNgn).toLocaleString()}` },
    { l: 'NET PLATFORM PROFIT', v: fmt(netProfit),   c: netProfit >= 0 ? '#4caf50' : '#ff4d4d', big: true },
    { l: 'ACTIVE MERCHANTS',    v: approvedMerchantCount, c: '#a8dadc', raw: true },
    { l: 'ACTIVE PREMIUM USERS', v: groomFinancesDoc.activePremiumUsers || 0, c: '#60a5fa', raw: true },
  ];

  return (
    <div style={S.content}>
      <TabHeader title="GLOBAL PLATFORM DASHBOARD" sub="Grooming + Marketplace · Combined view · Live data" />

      {/* Live rate badge */}
      {rateUpdatedAt && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: `${GOLD}10`, border: `1px solid ${GOLD}33`, borderRadius: '10px', padding: '8px 14px', marginBottom: '20px' }}>
          <span style={{ color: '#555', fontSize: '0.62rem', letterSpacing: '1px' }}>LIVE RATE</span>
          <span style={{ color: GOLD, fontWeight: '900', fontSize: '0.82rem' }}>$1 = ₦{Math.round(usdToNgn).toLocaleString()}</span>
          <span style={{ color: '#444', fontSize: '0.6rem' }}>
            Updated {rateUpdatedAt.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: '12px', marginBottom: '28px' }}>
        {statCards.map(s => (
          <div key={s.l} style={{ background: CARD_BG, borderRadius: '14px', padding: '16px', border: s.big ? `1px solid ${GOLD}55` : `1px solid ${BORDER}` }}>
            <p style={{ color: s.c, fontWeight: '900', fontSize: s.big ? '1.8rem' : '1.3rem', margin: '0 0 4px' }}>
              {s.raw ? Number(s.v).toLocaleString() : s.v}
            </p>
            <p style={{ color: '#444', fontSize: '0.6rem', letterSpacing: '1.5px', margin: '0 0 2px' }}>{s.l}</p>
            {s.note && <p style={{ color: '#2a2a2a', fontSize: '0.62rem', margin: 0 }}>{s.note}</p>}
          </div>
        ))}
      </div>

      {/* Grooming breakdown */}
      <SectionLabel>GROOMING REVENUE BREAKDOWN</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {[
          ['Booking Commissions', bookingComm,    '#60a5fa'],
          ['Monthly Subscriptions', monthlySubRev, '#00bbff'],
          ['Yearly Subscriptions',  yearlySubRev,  '#00d4ff'],
          ['Broadcasts',            broadcastTotal, '#f59e0b'],
          ['Style Promotions',      promotionTotal, '#a78bfa'],
        ].map(([l, v, c]) => (
          <div key={l} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '14px' }}>
            <p style={{ color: c, fontWeight: '900', fontSize: '1.05rem', margin: '0 0 4px' }}>{fmt(v)}</p>
            <p style={{ color: '#444', fontSize: '0.62rem', letterSpacing: '1px', margin: 0 }}>{l.toUpperCase()}</p>
          </div>
        ))}
      </div>

      {/* Marketplace breakdown */}
      <SectionLabel>MARKETPLACE REVENUE BREAKDOWN</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {[
          ['Platform Fees', mktFees, GOLD],
          ['Delivery Net',  mktDNet, '#60a5fa'],
          ['Total Marketplace Net', mktNet, GOLD],
        ].map(([l, v, c]) => (
          <div key={l} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '14px' }}>
            <p style={{ color: c, fontWeight: '900', fontSize: '1.05rem', margin: '0 0 4px' }}>{fmt(v)}</p>
            <p style={{ color: '#444', fontSize: '0.62rem', letterSpacing: '1px', margin: 0 }}>{l.toUpperCase()}</p>
          </div>
        ))}
      </div>

      {/* AI costs */}
      <SectionLabel>AI OPERATIONAL COSTS (@ $1 = ₦{Math.round(usdToNgn).toLocaleString()})</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {[
          ['🧊 Hyper3D Rodin',     rodinCount,  AI_COSTS_USD.rodin,  '#B8860B', rodinCostUSD,  rodinCostUSD * usdToNgn],
          ['✨ FLUX Fill Pro',     tryonCount,  AI_COSTS_USD.flux,   '#00d4ff', fluxCostUSD,   fluxCostUSD  * usdToNgn],
          ['🤖 Claude Sonnet',    tryonCount,  AI_COSTS_USD.claude, '#a78bfa', claudeCostUSD, claudeCostUSD* usdToNgn],
        ].map(([n, calls, rate, c, costUsd, costNgn]) => (
          <div key={n} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '16px' }}>
            <p style={{ color: c, fontWeight: '900', fontSize: '1.1rem', margin: '0 0 2px' }}>${costUsd.toFixed(2)}</p>
            <p style={{ color: 'white', fontSize: '0.82rem', margin: '0 0 2px', fontWeight: '600' }}>{n}</p>
            <p style={{ color: '#555', fontSize: '0.68rem', margin: '0 0 4px' }}>{calls.toLocaleString()} calls × ${rate}/call</p>
            <p style={{ color: '#ff4d4d', fontSize: '0.78rem', fontWeight: '700', margin: 0 }}>₦{Math.round(costNgn).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Total AI burn */}
      <div style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}33`, borderRadius: '12px', padding: '16px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#555', fontSize: '0.62rem', letterSpacing: '2px', margin: '0 0 4px' }}>TOTAL AI BURN — ALL TIME</p>
          <p style={{ color: GOLD, fontWeight: '900', fontSize: '0.78rem', margin: 0 }}>${totalAIusd.toFixed(2)} USD</p>
        </div>
        <p style={{ color: '#ff4d4d', fontWeight: '900', fontSize: '1.8rem', margin: 0 }}>₦{Math.round(totalAIngn).toLocaleString()}</p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SHARED UI PRIMITIVES
// ════════════════════════════════════════════════════════════
function Paginator({ currentPage, hasPrev, hasNext, goFirst, goPrev, goNext, rowCount, label }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${BORDER}`, flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
      <p style={{ color: '#444', fontSize: '0.7rem', margin: 0 }}>
        {label || `Page ${currentPage + 1} · ${rowCount} records · max ${PAGE_SIZE}/page`}
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {hasPrev && <button onClick={goFirst} style={pBtn}>« First</button>}
        <button onClick={goPrev} disabled={!hasPrev} style={{ ...pBtn, opacity: hasPrev ? 1 : 0.3 }}>‹ Prev</button>
        <span style={{ background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}44`, padding: '5px 13px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '900' }}>
          {currentPage + 1}
        </span>
        <button onClick={goNext} disabled={!hasNext} style={{ ...pBtn, opacity: hasNext ? 1 : 0.3 }}>Next ›</button>
      </div>
    </div>
  );
}

function Avatar({ src, name }) {
  return (
    <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: `${GOLD}15`, border: `1px solid ${GOLD}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
      {src
        ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
        : <span style={{ color: GOLD, fontWeight: '900', fontSize: '0.9rem' }}>{(name || '?')[0].toUpperCase()}</span>}
    </div>
  );
}

function TabHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
      <div>
        <h2 style={{ color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '0 0 4px', fontSize: '1.2rem' }}>{title}</h2>
        {sub && <p style={{ color: '#444', fontSize: '0.74rem', margin: 0 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}
function SectionLabel({ children, style }) {
  return <p style={{ color: GOLD, fontSize: '0.6rem', letterSpacing: '2px', fontWeight: '900', margin: '0 0 10px', ...style }}>{children}</p>;
}
function Empty({ icon, title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '36px', border: `1px dashed ${BORDER}`, borderRadius: '14px', marginBottom: '12px' }}>
      <p style={{ fontSize: '2.5rem', margin: '0 0 10px' }}>{icon}</p>
      <p style={{ color: GOLD, fontWeight: '900', margin: '0 0 6px', fontSize: '0.84rem' }}>{title}</p>
      <p style={{ color: '#444', fontSize: '0.78rem', margin: 0 }}>{sub}</p>
    </div>
  );
}
function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '22px 0' }}>
      <div style={{ width: '22px', height: '22px', border: `2px solid ${GOLD}22`, borderTop: `2px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#444', fontSize: '0.77rem', margin: 0 }}>Loading…</p>
    </div>
  );
}
function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: DEEP_BLACK, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}22`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
        <p style={{ color: GOLD, letterSpacing: '3px', marginTop: '16px', fontSize: '0.75rem' }}>LOADING MERCHANT ADMIN…</p>
      </div>
      <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const pBtn = { background: CARD_BG, border: `1px solid ${BORDER}`, color: '#888', padding: '6px 13px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '700', fontFamily: 'inherit' };

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  page:        { minHeight: '100vh', background: DEEP_BLACK, color: 'white', display: 'flex', fontFamily: "'Montserrat','Inter',sans-serif" },
  sidebar:     { width: '215px', minWidth: '215px', background: '#060c11', borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', padding: '22px 0', minHeight: '100vh', position: 'sticky', top: 0 },
  sideTop:     { textAlign: 'center', padding: '0 16px 18px', borderBottom: `1px solid ${BORDER}` },
  sideTitle:   { color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '8px 0 0', fontSize: '0.6rem' },
  nav:         { flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navBtn:      { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '10px', border: 'none', background: 'transparent', color: '#555', cursor: 'pointer', fontSize: '0.67rem', fontWeight: '700', letterSpacing: '0.8px', width: '100%', fontFamily: 'inherit' },
  navActive:   { background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}22` },
  navLink:     { background: 'transparent', border: 'none', color: '#444', cursor: 'pointer', fontSize: '0.66rem', padding: '7px 12px', textAlign: 'left', fontFamily: 'inherit', borderRadius: '8px', width: '100%' },
  badge:       { background: '#ff4d4d', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: '900' },
  main:        { flex: 1, overflowY: 'auto' },
  content:     { padding: '26px 30px', maxWidth: '1300px' },
  approvalCard:{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '16px' },
  prodCard:    { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '13px', display: 'flex', flexDirection: 'column' },
  rowCard:     { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  approveBtn:  { background: '#0a1f0a', color: '#4caf50', border: '1px solid #4caf5044', padding: '9px', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '0.74rem', fontFamily: 'inherit' },
  rejectBtn:   { background: '#1a0a0a', color: '#ff4d4d', border: '1px solid #ff4d4d44', padding: '9px', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '0.74rem', fontFamily: 'inherit' },
  outlineBtn:  { background: 'transparent', color: GOLD, padding: '8px 14px', borderRadius: '50px', fontWeight: '700', border: `1px solid ${GOLD}44`, cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit' },
  goldBtn:     { background: GOLD, color: 'black', padding: '10px 18px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'inherit' },
  exportBtn:   { background: 'transparent', border: `1px solid ${GOLD}44`, color: GOLD, padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit', fontWeight: '700' },
  pill:        { background: CARD_BG, border: `1px solid ${BORDER}`, color: '#555', padding: '6px 13px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit' },
  pillActive:  { background: `${GOLD}15`, border: `1px solid ${GOLD}44`, color: GOLD, fontWeight: '700' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '0.77rem', minWidth: '860px' },
  th:          { color: GOLD, fontSize: '0.58rem', letterSpacing: '1.2px', fontWeight: '900', padding: '9px 11px', textAlign: 'left', borderBottom: `1px solid ${BORDER}`, background: CARD_BG2, whiteSpace: 'nowrap' },
  td:          { color: '#888', padding: '10px 11px', borderBottom: `1px solid #0d152055`, fontSize: '0.75rem', verticalAlign: 'middle' },
  input:       { background: '#060c11', border: `1px solid ${BORDER}`, padding: '10px 13px', borderRadius: '10px', color: 'white', fontSize: '0.83rem', outline: 'none', fontFamily: 'inherit' },
  select:      { background: '#060c11', border: `1px solid ${BORDER}`, padding: '10px 13px', borderRadius: '10px', color: 'white', fontSize: '0.83rem', outline: 'none', fontFamily: 'inherit', appearance: 'none' },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal:       { background: DEEP_BLACK, border: `1px solid #ff4d4d33`, borderRadius: '18px', padding: '26px', width: '100%', maxWidth: '460px' },
};
